import { useState, useEffect } from "react";
import { select, selectAll } from "d3-selection";
import { useWindowWidth } from "components/utility/hooks/useMobileView";

const sectionHeight = 46;
function initViz(strategiesList, setSelectedIndex) {
	const strategiesViz = document.getElementById("page-strategies-viz-inline");
	const svgWidth = Math.min(1200, strategiesViz.offsetWidth);
	const svgHeight = strategiesList.length * sectionHeight;
	const svg = select("#page-strategies-viz-inline > svg")
		.attr("width", svgWidth)
		.attr("height", svgHeight);

	let polygonGroup;
	let polygonSubGroup;
	let lineGroup;
	let bgGroup;
	let yStep;
	let mainCoord;
	let secCoord;
	let bgCoord;
	const xStep = 7;

	svg.selectAll("*").remove();

	const selectedSvgs = strategiesViz.querySelectorAll("svg .selected, .desc.selected");
	selectedSvgs.forEach((label) => {
		label.classList.remove("selected");
	});

	bgGroup = svg.append("g");
	polygonGroup = svg.append("g");
	lineGroup = svg.append("g");

	strategiesList.forEach(function (strat, i) {
		polygonSubGroup = polygonGroup.append("g").attr("data-index", i).attr("class", "subgroup");

		mainCoord = [
			{ x: 0, y: i * sectionHeight },
			{ x: svgWidth / 2 - i * xStep, y: i * sectionHeight },
			{ x: svgWidth / 2 - (i + 1) * xStep, y: (i + 1) * sectionHeight },
			{ x: 0, y: (i + 1) * sectionHeight }
		];

		polygonSubGroup
			.append("polygon")
			.attr("points", function () {
				return mainCoord.map(function (coord) {
					return coord.x + "," + coord.y + " ";
				});
			})
			.attr("class", "main-text")
			.attr("fill", "rgba(5,5,3," + (i * 0.05 + 0.25) + ")");
		polygonSubGroup
			.append("text")
			.text(strat.title)
			.attr("class", "title t-label-lg-heavy")
			.attr("y", i * sectionHeight + (sectionHeight / 2 + 6))
			.attr("x", 20);

		[
			[0, 3],
			[0, 1],
			[1, 2]
		].forEach((coord) => {
			lineGroup
				.append("line")
				.attr("x1", mainCoord[coord[0]].x)
				.attr("y1", mainCoord[coord[0]].y)
				.attr("x2", mainCoord[coord[1]].x)
				.attr("y2", mainCoord[coord[1]].y);
		});

		yStep = svgWidth / (2 * strategiesList.length - 2);
		secCoord = [
			{ x: svgWidth / 2 - i * xStep, y: i * sectionHeight },
			{ x: svgWidth / 2 - (i + 1) * xStep, y: (i + 1) * sectionHeight },
			{
				x: svgWidth / 2 + (i + 1) * yStep,
				y: sectionHeight * strategiesList.length
			},
			{
				x: svgWidth / 2 + i * yStep,
				y: sectionHeight * strategiesList.length
			}
		];
		bgCoord = [
			{ x: secCoord[0].x, y: secCoord[0].y },
			{ x: svgWidth / 2 - xStep * strategiesList.length, y: secCoord[3].y },
			{ x: secCoord[3].x, y: secCoord[3].y }
		];
		if (i > 0) {
			polygonSubGroup
				.append("polygon")
				.attr("points", function () {
					return secCoord.map(function (coord) {
						return coord.x + "," + coord.y + " ";
					});
				})
				.attr("class", "highlight")
				.attr("fill", "none");

			bgGroup
				.append("polygon")
				.attr("points", function () {
					return bgCoord.map(function (coord) {
						return coord.x + "," + coord.y + " ";
					});
				})
				.attr("fill", "rgba(5,5,3," + 0.1 + ")");

			lineGroup
				.append("line")
				.attr("x1", secCoord[0].x)
				.attr("y1", secCoord[0].y)
				.attr("x2", secCoord[3].x)
				.attr("y2", secCoord[3].y);
		}

		if (i === strategiesList.length - 1) {
			lineGroup
				.append("line")
				.attr("x1", mainCoord[3].x)
				.attr("y1", mainCoord[3].y)
				.attr("x2", mainCoord[2].x)
				.attr("y2", mainCoord[2].y);

			lineGroup
				.append("line")
				.attr("x1", secCoord[1].x)
				.attr("y1", secCoord[1].y)
				.attr("x2", secCoord[2].x)
				.attr("y2", secCoord[2].y);
		}
	});

	selectAll(".subgroup")
		.on("mouseover", highlightStrategy)
		.on("mouseleave", removeHighlightStrategy);
	function highlightStrategy() {
		select(this).attr("class", "subgroup selected");
		setSelectedIndex(this.getAttribute("data-index"));
	}
	function removeHighlightStrategy() {
		select(this).attr("class", "subgroup");
		setSelectedIndex(null);
	}
}

const LegacyStrategiesViz = (props) => {
	const { title, description, subDescription, strategiesList = [] } = props.data || {};
	const [selectedIndex, setSelectedIndex] = useState(null);
	const windowWidth = useWindowWidth();

	useEffect(() => {
		initViz(strategiesList, setSelectedIndex);
	}, [windowWidth, strategiesList]);

	const toggleSelected = (index) => {
		setSelectedIndex(index === selectedIndex ? null : index);
	};

	return (
		<div className="strategies-container">
			<div className="strategies-title-desc">
				<div className="strategies-title t-heading-xl tc-cool-gray-01">{title}</div>
				<div className="strategies-desc t-body-md">{description}</div>
			</div>
			<div id="page-strategies-viz-inline" className="page-strategies-viz">
				{windowWidth >= 768 ? (
					<>
						<svg></svg>
						{selectedIndex && (
							<div className="desc t-body-md-heavy tc-cool-gray-02">
								{strategiesList[selectedIndex].desc}
							</div>
						)}
					</>
				) : (
					<div className="page-accordion">
						<ul className="strategies-mobile">
							{strategiesList.map((item, i) => (
								<li key={i} className={`${i === selectedIndex ? "selected" : ""}`}>
									<button
										className="strategy-label t-label-lg-heavy"
										onClick={() => toggleSelected(i)}
									>
										<span>{item.title}</span>
										<svg
											width="24"
											height="24"
											viewBox="0 0 24 24"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M6.34317 7.75732L4.92896 9.17154L12 16.2426L19.0711 9.17157L17.6569 7.75735L12 13.4142L6.34317 7.75732Z"
												fill="currentColor"
											/>
										</svg>
									</button>
									<div className="strategy-desc t-body-md-heavy tc-medium-cool-gray">
										{item.desc}
									</div>
								</li>
							))}
						</ul>
					</div>
				)}
			</div>
			<div className="strategies-sub-desc t-body-xs">{subDescription}</div>
		</div>
	);
};

export default LegacyStrategiesViz;
